import type { PathSnapshotTracker } from './path-snapshot-tracker.js';
import { extractAcpFileDiffsFromUpdate } from '../extract-acp-file-diffs-from-update/index.js';
import { extractToolTargetDisplayPaths } from '../extract-tool-target-paths.js';
import { accumulateToolPaths } from './tool-key.js';
import { sendExtractedDiffsAsSessionFileChanges, sendGitHeadVsWorkspaceForToolPaths } from './send-structured-file-changes.js';
import type { LogFn } from '../../../../log.js';

export function handleBridgeToolSessionUpdate(params: {
  params: unknown;
  updateKind: string;
  isToolUpdate: boolean;
  isCompletedToolCallUpdate: boolean;
  toolKey: string;
  toolPaths: string[];
  status: unknown;
  sessionParentPath: string | undefined;
  pathTracker: PathSnapshotTracker;
  sentFileChangePaths: Set<string>;
  send: ((msg: unknown) => void) | null;
  runId: string;
  sessionId: string;
  log: LogFn;
}): void {
  const {
    params: updateParams,
    updateKind,
    isToolUpdate,
    isCompletedToolCallUpdate,
    toolKey,
    toolPaths,
    status,
    sessionParentPath,
    pathTracker,
    sentFileChangePaths,
    send,
    runId,
    sessionId,
    log,
  } = params;

  if (updateKind === 'tool_call') {
    pathTracker.resetToolSnapshots(toolKey);
  }
  if (isToolUpdate) {
    accumulateToolPaths(toolKey, toolPaths, pathTracker.accumulatedPathsByToolKey);
  }

  if (isToolUpdate) {
    const deliver = send && runId && sessionId ? (msg: Parameters<NonNullable<typeof send>>[0]) => send(msg) : null;
    pathTracker.handleToolCallLifecycle(
      updateKind,
      toolKey,
      toolPaths,
      status,
      sessionParentPath,
      sentFileChangePaths,
      deliver,
      runId,
      sessionId,
      log,
    );
  }

  const diffs = extractAcpFileDiffsFromUpdate(updateParams, sessionParentPath);

  if (diffs.length > 0 && send && runId && sessionId) {
    sendExtractedDiffsAsSessionFileChanges(
      diffs,
      send,
      sessionParentPath,
      sessionId,
      runId,
      sentFileChangePaths,
      log,
    );
  } else if (diffs.length > 0) {
    log(
      `[Bridge service] Agent file diff(s) not forwarded (${diffs.length}): session or run not wired to the bridge.`,
    );
  }

  if (isCompletedToolCallUpdate && send && runId && sessionId) {
    const acc = pathTracker.accumulatedPathsByToolKey.get(toolKey);
    const merged = [...new Set([...(acc ? [...acc] : []), ...toolPaths])];
    sendGitHeadVsWorkspaceForToolPaths(
      merged,
      sentFileChangePaths,
      send,
      sessionParentPath,
      sessionId,
      runId,
      log,
    );
    pathTracker.accumulatedPathsByToolKey.delete(toolKey);
  }
}

export function resolveBridgeToolPaths(
  params: unknown,
  isToolUpdate: boolean,
  sessionParentPath: string | undefined,
): string[] {
  return isToolUpdate ? extractToolTargetDisplayPaths(params, sessionParentPath) : [];
}
